import { Injectable, OnDestroy } from "@angular/core";
import { Select } from "@ngxs/store";
import { AuthUser } from "app/core/auth/core/auth-user.model";
import { AuthState } from "app/core/auth/core/auth.state";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { map, switchMap, takeUntil } from "rxjs/operators";
import { environment } from "~env";
import { MaintenanceService } from "../maintenance/maintenance.service";
import { SystemRoles } from "../security/system-roles";

export interface BadgeItem {
  type: string;
  value: string;
}
export interface Saperator {
  name: string;
  type?: string;
}
export interface ChildrenItems {
  roles: string[];
  state: string;
  name: string;
  type?: string;
}

export interface Menu {
  state: string;
  name: string;
  type: string;
  icon: string;
  badge?: BadgeItem[];
  saperator?: Saperator[];
  roles: string[];
  children?: ChildrenItems[];
}

export const MENUITEMS = [
  {
    state: "home",
    name: "home.menu",
    type: "link",
    icon: "content_copy",
    roles: [],
  },
  {
    state: "company",
    name: "legalEntity.title",
    type: "link",
    icon: "location_city",
    roles: [
      SystemRoles.rolePrincipalRepresentative,
      SystemRoles.roleExternalRepresentative,
    ],
  },
  {
    state: 'continuing-education',
    name: 'formation.title',
    type: 'sub',
    icon: 'school',
    roles: [SystemRoles.roleMember],
    children:
      [
        {
          state: 'formation',
          name: 'formation.trainingRecord',
          type: 'link',
          roles: [SystemRoles.roleMember]
        },
        {
          state: 'exemptions',
          name: 'formation.exemptions',
          type: 'link',
          roles: [SystemRoles.roleMember]
        }
      ]
  },
  {
    state: "member-card",
    name: "memberCard.title",
    type: "link",
    icon: "card_membership",
    roles: [SystemRoles.roleMember],
  },
  {
    state: "student-card",
    name: "studentCard.title",
    type: "link",
    icon: "card_membership",
    roles: [SystemRoles.roleStudent],
  },
  {
    state: "addresses",
    name: "addresses.title",
    type: "link",
    icon: "location_on",
    roles: [SystemRoles.roleMember],
  },
  {
    state: "inscription",
    name: "inscription.title",
    type: "link",
    icon: "history_edu",
    roles: [SystemRoles.roleMember],
  },
  {
    state: "bills-invoices",
    name: "billsAndInvoices.title",
    type: "sub",
    icon: "attach_money",
    roles: [SystemRoles.roleMember],
    children: [
      {
        state: "bills",
        name: "bills.title",
        type: "link",
        roles: [SystemRoles.roleMember],
      },
      {
        state: "invoices",
        name: "invoices.title",
        type: "link",
        roles: [SystemRoles.roleMember],
      }
    ]
  },
  {
    state: "farp-subscription",
    name: "farpSubscription.title",
    type: "link",
    icon: "history_edu",
    roles: [SystemRoles.roleMember],
  },
  {
    state: "professional-services",
    name: "professionalServices.title",
    type: "sub",
    icon: "find_in_page",
    roles: [SystemRoles.roleMember],
    children: [
      {
        state: "surveys-to-complete",
        name: "professionalServices.links.surveysToComplete",
        type: "link",
        roles: [SystemRoles.roleMember],
      },
      {
        state: "surveys-history",
        name: "professionalServices.links.surveysHistory",
        type: "link",
        roles: [SystemRoles.roleMember],
      },
      {
        state: "documents",
        name: "professionalServices.links.documentsToShare",
        type: "link",
        roles: [SystemRoles.roleMember],
      },
    ],
  },
  {
    state: "dental-assistant",
    name: "dentalAssistant.title",
    type: "sub",
    icon: "person",
    roles: [
      SystemRoles.roleDentalAssistantUnconfirmed,
      SystemRoles.roleDentalAssistant,
    ],
    children: [
      {
        state: "assistant-annual-registration",
        name: "dentalAssistant.links.inscription",
        type: "link",
        roles: [
          SystemRoles.roleDentalAssistantUnconfirmed,
          SystemRoles.roleDentalAssistant,
        ],
      },
      {
        state: "addresses",
        name: "addresses.title",
        type: "link",
        icon: "location_on",
        roles: [
          SystemRoles.roleDentalAssistantUnconfirmed,
          SystemRoles.roleDentalAssistant,
        ],
      },
      {
        state: "dentalAssistant-card",
        name: "dentalAssistant.links.card",
        type: "link",
        icon: "card_membership",
        roles: [SystemRoles.roleDentalAssistant],
      },
      {
        state: "invoice-receipt",
        name: "dentalAssistant.invoicesReceipts",
        type: "link",
        icon: "attach_money",
        roles: [SystemRoles.roleDentalAssistantUnconfirmed],
      },
    ],
  },
  // {
  //   state: "shared-projects",
  //   name: "generalPublic.titleSharedProjects",
  //   type: "link",
  //   icon: "history_edu",
  //   roles: [SystemRoles.roleGeneralPublic],
  // },
  {
    state: "messages",
    name: "messages.menuTitle",
    type: "secure-messages",
    icon: "email",
    roles: [SystemRoles.roleGeneralPublic, SystemRoles.roleMember],
  },
];

@Injectable()
export class MenuItems implements OnDestroy {
  @Select(AuthState.signedIn) signedIn$: Observable<boolean>;
  @Select(AuthState.user) user$: Observable<AuthUser>;
  @Select(AuthState.inactiveMemberForSubscription) inactiveMemberForSubscription$: Observable<boolean>;

  private mainMenuItems = new BehaviorSubject<Menu[]>(
    MENUITEMS.filter((x) => x.roles.length === 0)
  );
  public mainMenuItems$ = this.mainMenuItems.asObservable();

  destroy$ = new Subject<void>();

  constructor(private maintenanceService: MaintenanceService) {
    let displayableMenus: Menu[];

    this.maintenanceService
      .getMaintenanceState()
      .pipe(
        takeUntil(this.destroy$),
        switchMap((state) => this.user$.pipe(map((user) => ({ state, user })))),
        switchMap(({ state, user }) => this.inactiveMemberForSubscription$.pipe(map((inactiveMemberForSubscription) => ({ state, user, inactiveMemberForSubscription }))))
      )
      .subscribe((maintenanceAndUser) => {
        displayableMenus = MENUITEMS.filter(
          (dm) =>
            dm.roles.filter((menuRole) =>
              maintenanceAndUser?.user?.roles.includes(menuRole)
            ).length > 0 || dm.roles.length === 0
        );

        if (!environment.inspectionEnabled) {
          const index = displayableMenus.findIndex(
            (d) => d.state == "professional-services"
          );
          if (index >= 0) {
            displayableMenus.splice(index, 1);
          }
        }

        if (!!maintenanceAndUser?.state) {
          // for each displayable menu, check if maintenanceAndUser.state has a falsy boolean, named with the displayableMenu.state
          displayableMenus = displayableMenus.filter(
            (dm) => !maintenanceAndUser.state[dm.state]
          );
        }

        //only menu subscription is active
        if (maintenanceAndUser.inactiveMemberForSubscription) {
          displayableMenus = displayableMenus.filter(
            (dm) => dm.state == "farp-subscription"
          );
        }

        this.mainMenuItems.next(displayableMenus);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasRoles(route: string, user: AuthUser) {
    return (
      user?.roles?.filter((ur) =>
        MENUITEMS.find((mi) => mi.state === route)?.roles?.includes(ur)
      ).length > 0
    );
  }
}
